// Encapsulates the File System Access API and IndexedDB persistence,
// providing a high-level Workspace interface for the app to read/write its DB.

const IDB_NAME = "arcoiris-filestore";
const STORE = "handles";
const KEY = "last";
const PICKER_ID = "arcoiris-db";
const DB_TYPES: FilePickerAcceptType[] = [
  {
    description: "Banco de dados Arco-Íris",
    accept: { "application/octet-stream": [".db"] },
  },
];

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    let done = false;
    const timer = setTimeout(() => {
      if (!done) {
        done = true;
        resolve(null);
      }
    }, ms);
    promise.then(
      (v) => {
        if (!done) {
          done = true;
          clearTimeout(timer);
          resolve(v);
        }
      },
      () => {
        if (!done) {
          done = true;
          clearTimeout(timer);
          resolve(null);
        }
      },
    );
  });
}

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(value: unknown): Promise<void> {
  const idb = await idbOpen();
  await new Promise<void>((resolve, reject) => {
    const tx = idb.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbGet(): Promise<FileSystemFileHandle | null> {
  const idb = await idbOpen();
  return new Promise((resolve, reject) => {
    const tx = idb.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = () =>
      resolve((req.result as FileSystemFileHandle | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
}

function rememberHandle(handle: FileSystemFileHandle): Promise<void | null> {
  return withTimeout(idbPut(handle), 1500);
}

function rememberedHandle(): Promise<FileSystemFileHandle | null> {
  return withTimeout(idbGet(), 1500);
}

export interface Workspace {
  readonly name: string;
  read(): Promise<Uint8Array>;
  write(bytes: Uint8Array): Promise<void>;
  saveBackup(bytes: Uint8Array): Promise<string | null>;
}

export class LocalWorkspace implements Workspace {
  private handle: FileSystemFileHandle;
  constructor(handle: FileSystemFileHandle) {
    this.handle = handle;
  }

  get name(): string {
    return this.handle.name;
  }

  async read(): Promise<Uint8Array> {
    return new Uint8Array(await (await this.handle.getFile()).arrayBuffer());
  }

  async write(bytes: Uint8Array): Promise<void> {
    const writable = await this.handle.createWritable();
    await writable.write(bytes as unknown as BufferSource);
    await writable.close();
  }

  async saveBackup(bytes: Uint8Array): Promise<string | null> {
    const stamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace("T", "_")
      .replaceAll(":", "-");
    const name = `arco-iris-backup-${stamp}.db`;
    const handle = await window.showSaveFilePicker({
      id: `${PICKER_ID}-backup`,
      suggestedName: name,
      types: DB_TYPES,
    });
    const writable = await handle.createWritable();
    await writable.write(bytes as unknown as BufferSource);
    await writable.close();
    return handle.name;
  }

  static supported(): boolean {
    return (
      typeof window !== "undefined" &&
      "showOpenFilePicker" in window &&
      "showSaveFilePicker" in window
    );
  }

  static async getRemembered(): Promise<{
    name: string;
    isGranted: boolean;
    reopen: () => Promise<LocalWorkspace | null>;
  } | null> {
    const handle = await rememberedHandle();
    if (!handle) return null;
    let isGranted = false;
    try {
      isGranted =
        (await handle.queryPermission({ mode: "readwrite" })) === "granted";
    } catch {
      // Permission query failed (e.g. denied or not supported)
    }

    return {
      name: handle.name,
      isGranted,
      reopen: async () => {
        const opts: FileSystemHandlePermissionDescriptor = { mode: "readwrite" };
        if (
          (await handle.queryPermission(opts)) === "granted" ||
          (await handle.requestPermission(opts)) === "granted"
        ) {
          await rememberHandle(handle);
          return new LocalWorkspace(handle);
        }
        return null;
      },
    };
  }

  static async createNew(suggestedName = "arco-iris.db"): Promise<LocalWorkspace> {
    const handle = await window.showSaveFilePicker({
      id: PICKER_ID,
      suggestedName,
      types: DB_TYPES,
    });
    await rememberHandle(handle);
    return new LocalWorkspace(handle);
  }

  static async openExisting(): Promise<LocalWorkspace> {
    const [handle] = await window.showOpenFilePicker({
      id: PICKER_ID,
      types: DB_TYPES,
      multiple: false,
    });
    await rememberHandle(handle!);
    return new LocalWorkspace(handle!);
  }
}
